// Moved out of pages/CanvasUI.tsx unchanged (Threads split).
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiError } from '@/lib/queryClient';
import { Activity, ChevronDown, ChevronLeft, Sparkles, User, X, Check, Plus, Search, MessageSquare, Loader2, Folder, Plane, UserRound } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/UserAvatar';
import { useToast } from '@/hooks/use-toast';
import { getFeatureFlags } from '@/config/featureFlags';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { scrollBehavior } from '@/lib/motion';
import { formatName, formatTripLabel } from '@/components/threads/ThreadContextPanel';
import AgenticErrorNotice from '@/components/threads/AgenticErrorNotice';
import { SlashCommandBlocks } from '@/components/threads/SlashCommandBlocks';
import { isAdminRole, isSystemAdminRole } from '@/lib/roles';
import ToolCallTimeline from '@/components/execution-trace/ToolCallTimeline';
import AgenticToolUsage from '@/components/execution-trace/AgenticToolUsage';
import { type ThreadsStreamingState } from '@/hooks/useThreadsStreaming';
import { AgentMessageList, type AgentThreadController } from '@/features/threads-agent';
import { StreamingMessage } from '@/components/threads/StreamingMessage';
import { StreamingMessageWrapper } from '@/components/threads/StreamingMessageWrapper';
import type { AgenticExecutionMetadata, AgenticToolCallLogEntry } from '@shared/agentic-execution';
import { type PendingActionDecisionResult, type PendingActionErrorState, type EditSelection, type ReplayRecord, normalizeIdentifier, coerceNumericId } from './canvasShared';
import { AdminDebugPanel } from './AdminDebugPanel';
import { PendingActionBubble, MessageContent, FeedbackControls, ReplayDetailsPanel, UnsplashGrid, VersionBadge } from './threadParts';

// ThreadsCanvas component - exact implementation from working SmartAssistant
export const ThreadsCanvas: React.FC<{
  sessionData: any;
  isWaitingForResponse: boolean;
  streamingRefetchDone: boolean;
  streamingState?: ThreadsStreamingState;
  simulatedStreamingContent?: string;
  /** AgentCore Threads (flag `agentcoreThreads`): renders the conversation with the new module. */
  agentThread?: AgentThreadController;
  providers: any[];
  selectedProvider: string;
  selectedModel: string;
  onProviderChange: (provider: string) => void;
  onModelChange: (model: string) => void;
  activeSessionId: number | null;
  projects?: any[];
  // hasActivities?: boolean; // Activity Stream - COMMENTED OUT FOR LATER
  // onOpenActivityStream?: () => void; // Activity Stream - COMMENTED OUT FOR LATER
  onBackToProject?: (projectId: number) => void;
  contactOptions: any[];
  tripOptions: any[];
  onLinkContact: (contactId: number) => void;
  onUnlinkContact: (contactId: number) => void;
  onLinkTrip: (tripId: number) => void;
  onUnlinkTrip: (tripId: number) => void;
  linkingContact?: boolean;
  linkingTrip?: boolean;
  onNewThread?: () => void;
  onResendPrompt?: (prompt: string) => void;
}> = ({
  sessionData,
  isWaitingForResponse,
  streamingRefetchDone,
  streamingState,
  simulatedStreamingContent,
  agentThread,
  providers,
  selectedProvider,
  selectedModel,
  onProviderChange,
  onModelChange,
  activeSessionId,
  projects = [],
  // hasActivities, // Activity Stream - COMMENTED OUT FOR LATER
  // onOpenActivityStream, // Activity Stream - COMMENTED OUT FOR LATER
  onBackToProject,
  contactOptions,
  tripOptions,
  onLinkContact,
  onUnlinkContact,
  onLinkTrip,
  onUnlinkTrip,
  linkingContact = false,
  linkingTrip = false,
  onNewThread,
  onResendPrompt,
}) => {
  const { user } = useAuth();
  const isSystemAdminUser = isSystemAdminRole(user?.role);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasScrolledToStreamingRef = useRef(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const queryClient = useQueryClient();
  const [editSelection, setEditSelection] = useState<EditSelection | null>(null);
  const [showCustomRewrite, setShowCustomRewrite] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const { toast } = useToast();
  const [pendingActionErrors, setPendingActionErrors] = useState<
    Record<number, PendingActionErrorState>
  >({});
  // Removed floating version pill (kept simple inline control only)
  const [traceInspector, setTraceInspector] = useState<{
    messageId: number;
    preview: string;
    correlationId?: string | null;
    toolCalls: AgenticToolCallLogEntry[];
    tokenUsage?: AgenticExecutionMetadata['tokenUsage'];
    cost?: AgenticExecutionMetadata['cost'];
  } | null>(null);
  const [feedbackDrafts, setFeedbackDrafts] = useState<
    Record<number, { rating: number; comment: string }>
  >({});
  const [expandedReplayPanels, setExpandedReplayPanels] = useState<
    Record<number, boolean>
  >({});
  const undoMutation = useMutation({
    mutationFn: async (messageId: number) => {
      return apiRequest('POST', `/api/smart-chat/messages/${messageId}/undo-last`);
    },
    onSuccess: (updated: any) => {
      queryClient.setQueryData([`/api/smart-chat/sessions/${sessionData?.session?.id || null}`], (old: any) => {
        if (!old) return old;
        const msgs = (old.messages || []).map((m: any) => (m.id === updated.id ? updated : m));
        return { ...old, messages: msgs };
      });
    },
  });

  const rewriteMutation = useMutation({
    mutationFn: async (payload: { messageId: number; selectedText: string; mode: string; instruction?: string }) => {
      return apiRequest('POST', `/api/smart-chat/messages/${payload.messageId}/rewrite-span`, payload);
    },
    onSuccess: (updated: any) => {
      queryClient.setQueryData(['/api/smart-chat/sessions', sessionData?.session?.id || activeSessionId], (old: any) => {
        if (!old) return old;
        const msgs = (old.messages || []).map((m: any) => (m.id === updated.id ? updated : m));
        return { ...old, messages: msgs };
      });
      setEditSelection(null);
      setCustomInstruction('');
      toast({ title: 'Updated', description: 'Selection rewritten.' });
    },
    onError: (err: any) => {
      const msg = err?.message || 'Rewrite failed';
      toast({ title: 'Rewrite failed', description: msg, variant: 'destructive' as any });
    }
  });

  const pendingActionMutation = useMutation<
    PendingActionDecisionResult,
    Error,
    { actionId: number; decision: 'approve' | 'cancel' }
  >({
    mutationFn: async ({ actionId, decision }) => {
      return apiRequest<PendingActionDecisionResult>(
        'POST',
        `/api/smart-chat/pending-actions/${actionId}/decision`,
        { decision },
      );
    },
    onSuccess: (result, variables) => {
      if (variables?.actionId) {
        setPendingActionErrors((prev) => {
          if (!prev[variables.actionId]) {
            return prev;
          }
          const next = { ...prev };
          delete next[variables.actionId];
          return next;
        });
      }
      const toastLabel =
        variables.decision === 'approve' ? 'Action approved' : 'Action cancelled';
      toast({
        title: toastLabel,
        description: result?.summary || 'The pending action was updated.',
      });
      if (sessionData?.session?.id) {
        queryClient.invalidateQueries({
          queryKey: ['/api/smart-chat/sessions', sessionData.session.id],
        });
      }
    },
    onError: (error, variables) => {
      let userMessage =
        (error?.message && typeof error.message === 'string'
          ? error.message
          : 'We couldn’t update this action.') || 'We couldn’t update this action.';
      let correlationId: string | undefined;
      let code: string | undefined;
      if (error instanceof ApiError) {
        const data = error.data;
        if (data && typeof data === 'object' && 'correlationId' in data) {
          const raw = (data as any).correlationId;
          correlationId = typeof raw === 'string' ? raw : undefined;
        }
        correlationId = correlationId ?? error.correlationId;
        if (data && typeof data === 'object') {
          const dataObj = data as Record<string, unknown>;
          if (typeof dataObj.userMessage === 'string') {
            userMessage = dataObj.userMessage;
          } else if (typeof dataObj.message === 'string') {
            userMessage = dataObj.message;
          }
          if (typeof dataObj.code === 'string') {
            code = dataObj.code;
          }
        }
      }
      const safeMessage =
        userMessage && userMessage.trim().length
          ? userMessage.trim()
          : 'We couldn’t update this action right now.';
      const toastDescription = correlationId
        ? `${safeMessage}${safeMessage.endsWith('.') ? '' : '.'} Please contact support with ID ${correlationId}.`
        : safeMessage;
      const actionId = variables?.actionId ?? currentPendingActionId;
      if (typeof actionId === 'number') {
        setPendingActionErrors((prev) => ({
          ...prev,
          [actionId]: {
            message: safeMessage,
            correlationId,
            code,
          },
        }));
      }
      toast({
        title: 'Unable to update action',
        description: toastDescription,
        variant: 'destructive' as any,
      });
    },
  });
  const currentPendingActionId = (pendingActionMutation.variables as
    | { actionId?: number }
    | undefined)?.actionId;

  const handlePendingActionDecision = useCallback(
    (actionId: number, decision: 'approve' | 'cancel') => {
      setPendingActionErrors((prev) => {
        if (!prev[actionId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[actionId];
        return next;
      });
      pendingActionMutation.mutate({ actionId, decision });
    },
    [pendingActionMutation],
  );

  function handleRewrite(mode: 'shorter' | 'formal' | 'grammar' | 'custom') {
    if (!editSelection || !sessionData?.session?.id) return;
    const payload: any = { messageId: editSelection.messageId, selectedText: editSelection.text, mode };
    if (mode === 'custom' && customInstruction.trim()) payload.instruction = customInstruction.trim();
    rewriteMutation.mutate(payload);
  }

  const coerceMessageId = useCallback((value: unknown): number | null => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }, []);

  const buildTracePreview = useCallback((message: any): string => {
    if (!message) {
      return 'Assistant response';
    }
    const metadataTitle =
      typeof message?.metadata?.title === 'string'
        ? message.metadata.title.trim()
        : '';
    const content =
      typeof message?.content === 'string' ? message.content.trim() : '';
    const source = metadataTitle || content;
    if (!source) {
      return 'Assistant response';
    }
    return source.length > 160 ? `${source.slice(0, 157)}...` : source;
  }, []);

  const handleTraceDetailsOpen = useCallback(
    (message: any, execution: AgenticExecutionMetadata | null) => {
      if (!isSystemAdminUser || !execution?.toolCalls?.length) {
        return;
      }
      const numericId = coerceMessageId(message?.id);
      if (numericId === null) {
        return;
      }
      setTraceInspector({
        messageId: numericId,
        preview: buildTracePreview(message),
        correlationId: execution.correlationId ?? null,
        toolCalls: execution.toolCalls,
        tokenUsage: execution.tokenUsage,
        cost: execution.cost,
      });
    },
    [buildTracePreview, coerceMessageId, isSystemAdminUser],
  );

  const handleTraceDetailsClose = useCallback(() => {
    setTraceInspector(null);
  }, []);

  useEffect(() => {
    if (!isSystemAdminUser) {
      setTraceInspector(null);
    }
  }, [isSystemAdminUser]);

  const featureFlags = getFeatureFlags();
  const {
    isEnabled: isResolvedFeatureFlagEnabled,
    flags: resolvedFeatureFlags,
    isLoading: isLoadingResolvedFeatureFlags,
    error: resolvedFeatureFlagsError,
  } = useResolvedFeatureFlags();
  // AIVIA threads show no CRM items (linked trips and clients) until CRM launches for the advisor.
  // Hidden until the flags have loaded, so CRM items never flash before the answer arrives.
  const crmHidden =
    isLoadingResolvedFeatureFlags ||
    (isResolvedFeatureFlagEnabled('agentcoreThreads') && !isResolvedFeatureFlagEnabled('agentcoreCrmTools'));
  // The backend is the single source of truth for this flag (it evaluates
  // FEATURE_THREAD_REPLAY_FEEDBACK + rollout), so once it has answered we honour
  // it — including an explicit `false`. The build-time VITE_ flag is only a
  // fallback for while that answer is still loading or the request failed.
  const hasResolvedFeatureFlags =
    !isLoadingResolvedFeatureFlags &&
    !resolvedFeatureFlagsError &&
    'threadReplayFeedback' in resolvedFeatureFlags;
  const showFeedbackUi = hasResolvedFeatureFlags
    ? isResolvedFeatureFlagEnabled('threadReplayFeedback')
    : Boolean(featureFlags.threadReplayFeedback);
  const currentSessionId = sessionData?.session?.id ?? activeSessionId ?? null;
  // Flagged users still send external-mode and slash-command turns through the legacy engine;
  // those replies keep the legacy streaming view instead of the AgentCore pending card.
  const legacyReplyLive =
    !agentThread?.turn &&
    ((streamingState && streamingState.phase !== 'idle') || simulatedStreamingContent !== undefined);
  const {
    data: replaySummaries,
    isLoading: isLoadingReplays,
    refetch: refetchReplays,
  } = useQuery({
    queryKey: ['smart-chat-replays', currentSessionId],
    enabled: Boolean(currentSessionId && showFeedbackUi),
    queryFn: async () => {
      if (!currentSessionId) return null;
      return apiRequest(
        'GET',
        `/api/smart-chat/replays?sessionId=${currentSessionId}&includeFeedback=true`,
      );
    },
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: async ({
      messageId,
      rating,
      comment,
    }: {
      messageId: number;
      rating: number;
      comment?: string;
    }) => {
      return apiRequest('POST', `/api/smart-chat/messages/${messageId}/feedback`, {
        rating,
        comment,
      });
    },
    onSuccess: (_data, variables) => {
      toast({
        title: 'Feedback saved',
        description: 'Thanks for letting us know how this response performed.',
      });
      setFeedbackDrafts((previous) => {
        if (!previous[variables.messageId]) {
          return previous;
        }
        const next = { ...previous };
        delete next[variables.messageId];
        return next;
      });
      void refetchReplays();
    },
    onError: (error: any) => {
      toast({
        title: 'Unable to save feedback',
        description:
          error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    setFeedbackDrafts({});
  }, [currentSessionId, showFeedbackUi]);

  useEffect(() => {
    setExpandedReplayPanels({});
  }, [currentSessionId]);

  useEffect(() => {
    if (!Array.isArray(sessionData?.messages)) {
      setPendingActionErrors((prev) => {
        return Object.keys(prev).length ? {} : prev;
      });
      return;
    }
    const activeIds = new Set<number>();
    sessionData.messages.forEach((message: any) => {
      const actions = message?.metadata?.pendingActions;
      if (!Array.isArray(actions)) {
        return;
      }
      actions.forEach((action: any) => {
        if (typeof action?.id === 'number') {
          activeIds.add(action.id);
        }
      });
    });
    setPendingActionErrors((prev) => {
      const nextEntries = Object.entries(prev).filter(([id]) =>
        activeIds.has(Number(id)),
      );
      if (nextEntries.length === Object.keys(prev).length) {
        return prev;
      }
      if (nextEntries.length === 0) {
        return Object.keys(prev).length ? {} : prev;
      }
      const next: Record<number, PendingActionErrorState> = {};
      nextEntries.forEach(([id, value]) => {
        next[Number(id)] = value;
      });
      return next;
    });
  }, [sessionData?.messages]);

  const replayDetailsByMessageId = useMemo(() => {
    if (!showFeedbackUi || !replaySummaries?.data) {
      return {} as Record<number, ReplayRecord>;
    }
    const map: Record<number, ReplayRecord> = {};
    replaySummaries.data.forEach((entry: ReplayRecord) => {
      if (entry?.assistantMessageId) {
        map[entry.assistantMessageId] = entry;
      }
    });
    return map;
  }, [replaySummaries, showFeedbackUi]);

  const replayFeedbackMap = useMemo(() => {
    if (!showFeedbackUi) {
      return {} as Record<number, { rating: number; comment?: string | null }>;
    }
    const map: Record<number, { rating: number; comment?: string | null }> = {};
    Object.entries(replayDetailsByMessageId).forEach(([key, entry]) => {
      const latestFeedback = Array.isArray(entry.feedback)
        ? entry.feedback[0]
        : null;
      if (latestFeedback) {
        map[Number(key)] = latestFeedback;
      }
    });
    return map;
  }, [replayDetailsByMessageId, showFeedbackUi]);

  const canViewReplayDetails = useMemo(() => {
    if (!showFeedbackUi || !user) {
      return false;
    }
    const isOwner = sessionData?.session?.userId === user.id;
    const isAdmin = isAdminRole(user?.role);
    return isOwner || isAdmin;
  }, [showFeedbackUi, user, sessionData?.session?.userId]);

  const handleToggleReplayPanel = useCallback((messageId: number) => {
    setExpandedReplayPanels((previous) => ({
      ...previous,
      [messageId]: !previous[messageId],
    }));
  }, []);

  const handleFeedbackDraftChange = useCallback(
    (
      messageId: number,
      updates: Partial<{ rating: number; comment: string }>,
    ) => {
      setFeedbackDrafts((prev) => {
        const fallback = replayFeedbackMap[messageId];
        const current = prev[messageId] ?? {
          rating: fallback?.rating ?? 0,
          comment: fallback?.comment ?? '',
        };
        return {
          ...prev,
          [messageId]: {
            rating:
              typeof updates.rating === 'number'
                ? updates.rating
                : current.rating,
            comment:
              typeof updates.comment === 'string'
                ? updates.comment
                : current.comment,
          },
        };
      });
    },
    [replayFeedbackMap],
  );

  const handleFeedbackSubmit = useCallback(
    (messageId: number) => {
      if (!showFeedbackUi) return;
      const existing = replayFeedbackMap[messageId];
      const draft = feedbackDrafts[messageId];
      const ratingToSend =
        draft?.rating && draft.rating > 0
          ? draft.rating
          : existing?.rating ?? 0;
      if (!ratingToSend) {
        toast({
          title: 'Select a rating',
          description: 'Pick a score between 1 and 10 before submitting.',
        });
        return;
      }
      const rawComment = draft?.comment ?? existing?.comment ?? '';
      submitFeedbackMutation.mutate({
        messageId,
        rating: ratingToSend,
        comment: rawComment?.trim()?.length ? rawComment.trim() : undefined,
      });
    },
    [
      feedbackDrafts,
      replayFeedbackMap,
      showFeedbackUi,
      submitFeedbackMutation,
      toast,
    ],
  );

  async function restoreVersion(messageId: number, versionIndex: number) {
    try {
      const prevY = window.scrollY;
      const updated = await apiRequest('POST', `/api/smart-chat/messages/${messageId}/restore-version`, { versionNumber: versionIndex });
      queryClient.setQueryData(['/api/smart-chat/sessions', sessionData?.session?.id || activeSessionId], (old: any) => {
        if (!old) return old;
        const msgs = (old.messages || []).map((m: any) => (m.id === updated.id ? updated : m));
        return { ...old, messages: msgs };
      });
      toast({ title: 'Version restored', description: `Switched to version ${versionIndex + 1}.` });
      // Keep user at the same visual position
      requestAnimationFrame(() => window.scrollTo(0, prevY));
    } catch (e: any) {
      toast({ title: 'Restore failed', description: e?.message || 'Could not restore version', variant: 'destructive' as any });
    }
  }

  // Removed floating version pill tracking

  // Track message count to detect when user sends a new message
  const prevMessageCountRef = useRef<number>(sessionData?.messages?.length || 0);

  // Auto-scroll behavior:
  // - When user sends a new message, scroll to show the start of the AI response area
  // - Do NOT auto-scroll when streaming ends - keep the user's scroll position
  useEffect(() => {
    const currentMessageCount = sessionData?.messages?.length || 0;
    const messageCountChanged = currentMessageCount !== prevMessageCountRef.current;
    
    // Only scroll when user sends a new message (message count increases from user action)
    // and we're starting to wait for a response
    if (isWaitingForResponse && !hasScrolledToStreamingRef.current) {
      // Small delay to ensure the streaming message container is rendered
      const timeout = setTimeout(() => {
        if (streamingMessageRef.current) {
          streamingMessageRef.current.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
          hasScrolledToStreamingRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
    
    // Reset scroll tracking when response completes
    if (!isWaitingForResponse) {
      hasScrolledToStreamingRef.current = false;
      prevMessageCountRef.current = currentMessageCount;
    }
    
    try {
      (window as any).__currentSessionMessages = sessionData?.messages || [];
    } catch {}
  }, [sessionData?.messages, isWaitingForResponse]);

  // AR-210 (TC-34.5): nothing told a screen-reader user that AIVIA was working
  // or that the answer had arrived. One polite status line for both.
  const [responseStatus, setResponseStatus] = useState('');
  const wasWaitingRef = useRef(false);
  useEffect(() => {
    if (isWaitingForResponse && !wasWaitingRef.current) setResponseStatus('AIVIA is thinking…');
    if (!isWaitingForResponse && wasWaitingRef.current) setResponseStatus('AIVIA responded');
    wasWaitingRef.current = isWaitingForResponse;
  }, [isWaitingForResponse]);

  // Keyboard shortcut for New Thread (Cmd/Ctrl+N)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n' && onNewThread) {
        e.preventDefault();
        onNewThread();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onNewThread]);

  // Scroll-to-bottom detection: show button when user is not at the bottom
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    // Find the actual scrollable viewport inside ScrollArea
    const viewport = scrollArea.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!viewport) return;

    const checkScrollPosition = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Show button if more than 150px from bottom
      setShowScrollToBottom(distanceFromBottom > 150);
    };

    // Initial check with delay to let content render
    const initialTimeout = setTimeout(checkScrollPosition, 100);

    // Listen to scroll events
    viewport.addEventListener('scroll', checkScrollPosition);
    
    // Also check when content changes
    // [FIX] Track mutation observer timeout to prevent leaks on unmount
    let mutationTimeout: ReturnType<typeof setTimeout> | null = null;
    const observer = new MutationObserver(() => {
      // Debounce mutation checks - clear previous timeout first
      if (mutationTimeout) {
        clearTimeout(mutationTimeout);
      }
      mutationTimeout = setTimeout(checkScrollPosition, 50);
    });
    observer.observe(viewport, { childList: true, subtree: true, characterData: true });

    // Periodic check during streaming (catches edge cases)
    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (isWaitingForResponse) {
      intervalId = setInterval(checkScrollPosition, 500);
    }

    // Also check on window resize
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      clearTimeout(initialTimeout);
      // [FIX] Clear mutation observer timeout to prevent firing after unmount
      if (mutationTimeout) {
        clearTimeout(mutationTimeout);
      }
      viewport.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
    };
  // Note: simulatedStreamingContent intentionally NOT included here.
  // It updates 60+ times/sec during animation, which would destroy/recreate
  // all listeners on every frame. The MutationObserver already detects DOM changes.
  }, [sessionData?.messages, isWaitingForResponse]);

  // Scroll to bottom function
  const handleScrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: scrollBehavior() });
    setShowScrollToBottom(false);
  }, []);

  // Validate session data structure
  if (!sessionData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No active session</h2>
          <p className="text-gray-500">Submit a command to start a new chat session</p>
        </div>
      </div>
    );
  }

  // Check if sessionData is actually a session object with required properties
  if (Array.isArray(sessionData) || typeof sessionData !== 'object' || !('session' in sessionData || 'messages' in sessionData)) {
    console.warn('Invalid session data structure received:', {
      isArray: Array.isArray(sessionData),
      type: typeof sessionData,
      hasSession: sessionData && 'session' in sessionData,
      hasMessages: sessionData && 'messages' in sessionData,
      data: sessionData
    });
    
    // Auto-refetch session data to try recovery
    const handleRefresh = () => {
      if (activeSessionId) {
        queryClient.invalidateQueries({ 
          queryKey: ['/api/smart-chat/sessions', activeSessionId] 
        });
      }
    };
    
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Session data loading...</h2>
          <p className="text-gray-500 mb-4">
            The session data is being retrieved. This may take a moment.
          </p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
          >
            Refresh Session
          </button>
        </div>
      </div>
    );
  }

  const { session, messages } = sessionData;
  const linkedContacts = sessionData?.linkedContacts || [];
  const linkedTrips = sessionData?.linkedTrips || [];
  const toolRuns = sessionData?.toolRuns || [];
  const normalizedLinkedContacts = (linkedContacts || []).map((link: any) => {
    const contactId = normalizeIdentifier(link.contactId ?? link.clientId ?? link.id);
    return {
      ...link,
      linkId: link.linkId ?? link.id,
      contactId,
      clientId: link.clientId ?? contactId,
      id: contactId,
    };
  });
  const normalizedLinkedTrips = (linkedTrips || []).map((link: any) => {
    const tripId = normalizeIdentifier(link.tripId ?? link.id);
    return {
      ...link,
      linkId: link.linkId ?? link.id,
      tripId,
      id: tripId,
    };
  });
  
  // Find the project for this session
  // One message from the existing engine. Also used by the AgentCore list for non-AgentCore messages.
  const renderMessage = (message: any) => {
    const agenticExecution = (message?.metadata?.agenticExecution ?? null) as AgenticExecutionMetadata | null;
    const toolCallLog = Array.isArray(agenticExecution?.toolCalls)
      ? (agenticExecution?.toolCalls as AgenticToolCallLogEntry[])
      : [];
    const hasPostHocTrace = toolCallLog.length > 0;
    const rawAgenticError = (message?.metadata?.agenticError ?? agenticExecution?.error ?? null) as AgenticExecutionMetadata['error'] | null;
    const toolBadgeCount =
      agenticExecution?.toolSummary?.uniqueTools?.length ??
      agenticExecution?.toolsUsed?.length ??
      0;
    const showAgenticFooter = Boolean(
      agenticExecution &&
        (toolBadgeCount > 0 || agenticExecution.cta?.text),
    );
    const numericMessageId = coerceMessageId(message.id);
    const canShowTraceDetails =
      isSystemAdminUser &&
      message.role === 'assistant' &&
      hasPostHocTrace &&
      numericMessageId !== null;
    const canRenderReplayUi =
      message.role === 'assistant' && numericMessageId !== null;
    const existingFeedback =
      showFeedbackUi && canRenderReplayUi
        ? replayFeedbackMap[numericMessageId]
        : undefined;
    const replayDetails =
      showFeedbackUi && canRenderReplayUi
        ? replayDetailsByMessageId[numericMessageId]
        : undefined;
    const feedbackDraft =
      showFeedbackUi && canRenderReplayUi
        ? feedbackDrafts[numericMessageId] ?? {
            rating: existingFeedback?.rating ?? 0,
            comment: existingFeedback?.comment ?? '',
          }
        : null;
    const isSubmittingFeedback =
      showFeedbackUi &&
      canRenderReplayUi &&
      submitFeedbackMutation.isPending &&
      submitFeedbackMutation.variables?.messageId ===
        numericMessageId;
    const shouldShowReplayPanel =
      canViewReplayDetails && canRenderReplayUi;

    return (
      <div
        key={message.id}
        className={`relative mb-6 ${
          message.role === 'assistant'
            ? 'bg-white dark:bg-gray-800'
            : 'bg-gray-100 dark:bg-gray-700'
        } ${
          message.role === 'assistant' && typeof message?.metadata?.currentVersionIndex === 'number' && message.metadata.currentVersionIndex > 0
            ? 'border border-orange-500'
            : ''
        } rounded-lg p-4 ${
          message.role === 'user' ? 'ml-[20%]' : ''
        }`}
        onMouseUp={(e) => {
          if (message.role !== 'assistant') return;
          const sel = window.getSelection();
          if (!sel || sel.isCollapsed) return;
          const selected = sel.toString();
          if (!selected || !selected.trim()) return;
          const range = sel.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          setEditSelection({
            messageId: Number(message.id),
            text: selected,
            rect: { top: rect.top + window.scrollY, left: rect.left + window.scrollX },
          });
        }}
      >
        <div className="flex items-start gap-3">
          {message.role === 'user' && (
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
            <UserAvatar
              email={user?.email || ''}
              name={user?.displayName || 'User'}
              avatarUrl={user?.avatarUrl || ''}
              size={32}
              className=""
            />
          </div>
        )}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className={`prose max-w-none break-words overflow-wrap-anywhere dark:prose-invert relative`}>
            {message.role === 'assistant' && (
              <VersionBadge 
                message={message} 
                onRestore={(v) => restoreVersion(message.id, v)}
              />
            )}
            {message.role === 'assistant' && message?.metadata?.crmCommand && (
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-600">
                <Badge variant="outline" className="uppercase tracking-wide">
                  {message.metadata.crmMode === 'write' ? 'CRM Pending Change' : 'CRM Search'}
                </Badge>
                {typeof message.metadata.crmQuery === 'string' && message.metadata.crmQuery.length > 0 && (
                  <span className="text-slate-500">"{message.metadata.crmQuery}"</span>
                )}
              </div>
            )}
          <MessageContent content={message.content} />
          {message.role === 'assistant' && (
            <SlashCommandBlocks metadata={message.metadata} />
          )}
          {message.role === 'assistant' &&
            Array.isArray(message?.metadata?.pendingActions) &&
            message.metadata.pendingActions.length > 0 && (
              <PendingActionBubble
                actions={message.metadata.pendingActions as Array<{
                  id: number;
                  summary: string;
                  status?: string;
                }>}
                onDecision={handlePendingActionDecision}
                isActionProcessing={(actionId) =>
                  pendingActionMutation.isPending &&
                  currentPendingActionId === actionId
                }
                failedActions={pendingActionErrors}
              />
            )}
            {message.role === 'assistant' && message?.metadata?.unsplash?.images?.length > 0 && (
              <UnsplashGrid meta={message.metadata} />
            )}
          </div>
          {message.role === 'assistant' && isSystemAdminUser && (
            <AgenticErrorNotice
              error={
                rawAgenticError &&
                rawAgenticError.summary?.stage === 'tool' &&
                toolCallLog.some((call) => call.status === 'success')
                  ? null
                  : rawAgenticError
              }
              correlationId={
                (message.metadata?.fallback?.correlationId as string | undefined) ??
                agenticExecution?.correlationId ??
                null
              }
              isAdmin={isSystemAdminUser}
            />
          )}
          {message.role === 'assistant' && isSystemAdminUser && (
            <AdminDebugPanel metadata={message.metadata} messageId={message.id} />
          )}
          {message.role === 'assistant' && isSystemAdminUser && showAgenticFooter && (
            <AgenticToolUsage
              execution={agenticExecution}
              showCta={Boolean(agenticExecution?.cta?.text)}
              className="mt-3"
              onOpenTrace={
                canShowTraceDetails
                  ? () => handleTraceDetailsOpen(message, agenticExecution)
                  : undefined
              }
            />
          )}
          {canRenderReplayUi && showFeedbackUi && feedbackDraft && (
            <FeedbackControls
              rating={feedbackDraft.rating}
              comment={feedbackDraft.comment}
              onRatingChange={(score) =>
                handleFeedbackDraftChange(numericMessageId!, {
                  rating: score,
                })
              }
              onCommentChange={(value) =>
                handleFeedbackDraftChange(numericMessageId!, {
                  comment: value,
                })
              }
              onSubmit={() => handleFeedbackSubmit(numericMessageId!)}
              existingFeedback={existingFeedback}
              isSubmitting={Boolean(isSubmittingFeedback)}
            />
          )}
          {isSystemAdminUser && shouldShowReplayPanel && numericMessageId !== null && (
            <ReplayDetailsPanel
              replay={replayDetails}
              isExpanded={Boolean(
                expandedReplayPanels[numericMessageId],
              )}
              isLoading={isLoadingReplays}
              onToggle={() => handleToggleReplayPanel(numericMessageId)}
            />
          )}
        </div>
      </div>
       {message.role === 'assistant' && (
        <button
          className="absolute bottom-2 right-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Copy response"
          onClick={() => navigator.clipboard.writeText(message.content)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gray-500">
            <path d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z"/>
          </svg>
        </button>
      )}
    </div>
    );
  };

  const currentProject = session?.chatProjectId 
    ? projects.find(p => p.id === session.chatProjectId)
    : null;
  
  return (
    <div className="h-full flex flex-col relative">
      {/* Header with provider/model selection */}
      <div className="sticky top-0 z-20 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 pr-3 pl-3 md:pr-20 md:pl-12">
        <div className="flex items-center justify-between gap-2">
          {/* AR-316: the title's own `min-w-0 truncate` only bites if this row
              can shrink too, otherwise the row sizes to the untruncated title
              and pushes the header actions off-screen. */}
          <div className="flex min-w-0 items-center gap-3">
                         {/* Project name with back arrow (if thread is part of a project) */}
             {currentProject && (
               <div className="flex items-center gap-2 mb-1">
                 <button
                   onClick={() => onBackToProject?.(currentProject.id)}
                   className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 py-0.5 transition-colors group"
                   title={`Back to ${currentProject.name} project`}
                 >
                   <ChevronLeft className="w-3 h-3 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                   <Folder className="w-3 h-3 group-hover:opacity-80" style={{ color: currentProject.color }} />
                   <span className="text-sm text-gray-500 dark:text-gray-400 font-medium group-hover:text-gray-700 dark:group-hover:text-gray-200">{currentProject.name}</span>
                 </button>
               </div>
             )}
            <h2 className="min-w-0 truncate text-lg font-semibold text-gray-900 dark:text-gray-100 md:ml-12">{session?.title || 'Chat'}</h2>
            {(user as any)?.isAdmin && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {session?.provider} • {session?.model}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2 md:mr-4">
            {/* New Thread button */}
            {onNewThread && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={onNewThread}
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 gap-2 hidden sm:flex"
                      title="New Thread (Cmd/Ctrl+N)"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="hidden md:inline">New Thread</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New Thread (Cmd/Ctrl+N)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Mobile New Thread button (icon only) */}
            {onNewThread && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onNewThread}
                      className="sm:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="New Thread"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New Thread</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Activity Stream trigger in header row - COMMENTED OUT FOR LATER */}
            {/* <button
              onClick={() => { if (onOpenActivityStream) onOpenActivityStream(); }}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative"
              title="Activity Stream"
            >
              <Sparkles className="w-5 h-5" />
              {!!hasActivities && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-600 rounded-full border-2 border-white"></span>
              )}
            </button> */}
          </div>
        </div>
        
        {/* Linked Entity Badges - show in header */}
        {!crmHidden && (normalizedLinkedContacts.length > 0 || normalizedLinkedTrips.length > 0) && (
          <div className="flex flex-wrap gap-2 items-center text-sm mt-2 px-3">
            {normalizedLinkedContacts.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                  <UserRound className="w-3 h-3" />
                  Clients:
                </span>
                {normalizedLinkedContacts.map((contact: any) => {
                  const contactId = contact.contactId ?? contact.clientId ?? contact.id;
                  return (
                    <Badge
                      key={`${contactId}-${contact.linkedAt}`}
                      variant="secondary"
                      className="flex items-center gap-1 text-xs"
                    >
                      <span>{formatName(contact)}</span>
                      <Button
                        variant="ghost"
                        size="icon" aria-label={`Remove ${formatName(contact)}`}
                        className="h-3 w-3 p-0 hover:bg-gray-300"
                        onClick={() => {
                          const numericId = coerceNumericId(contact.contactId ?? contact.id);
                          if (numericId === null) {
                            console.warn('Unable to parse contact id for unlinking', contact);
                            return;
                          }
                          onUnlinkContact(numericId);
                        }}
                      >
                        <X className="w-2.5 h-2.5" />
                      </Button>
                    </Badge>
                  );
                })}
              </div>
            )}
            {normalizedLinkedTrips.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                  <Plane className="w-3 h-3" />
                  Trips:
                </span>
                {normalizedLinkedTrips.map((trip: any) => (
                  <Badge
                    key={`${trip.tripId}-${trip.linkedAt}`}
                    variant="secondary"
                    className="flex items-center gap-1 text-xs"
                  >
                    <span>{formatTripLabel(trip)}</span>
                      <Button
                        variant="ghost"
                        size="icon" aria-label={`Remove ${formatTripLabel(trip)}`}
                        className="h-3 w-3 p-0 hover:bg-gray-300"
                        onClick={() => {
                          const numericId = coerceNumericId(trip.tripId ?? trip.id);
                          if (numericId === null) {
                            console.warn('Unable to parse trip id for unlinking', trip);
                            return;
                          }
                          onUnlinkTrip(numericId);
                        }}
                      >
                      <X className="w-2.5 h-2.5" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {responseStatus}
      </div>
      {/* Messages using ScrollArea for better performance */}
      <ScrollArea className="flex-1" ref={scrollAreaRef}>
        <div className="p-6">
          {!messages ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : messages?.length === 0 ? (
            (() => {
              const originalPrompt = (session?.metadata as any)?.originalPrompt as string | undefined;
              const isOrphaned = originalPrompt && session?.title && session.title !== 'New Chat';
              if (isOrphaned && onResendPrompt) {
                return (
                  <div className="flex flex-col items-center justify-center h-full text-center p-6">
                    <MessageSquare className="h-12 w-12 text-orange-300 dark:text-orange-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Message wasn't saved</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-1">
                      This thread was started but the initial message failed to send.
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 max-w-sm italic mb-4 line-clamp-3">
                      "{originalPrompt.length > 120 ? originalPrompt.slice(0, 120) + '...' : originalPrompt}"
                    </p>
                    <button
                      onClick={() => onResendPrompt(originalPrompt)}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                    >
                      Resend Message
                    </button>
                  </div>
                );
              }
              return (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <MessageSquare className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No messages yet</h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                    Start the conversation by sending a message below.
                  </p>
                </div>
              );
            })()
          ) : (
            <div
              className="max-w-4xl mx-auto py-6 px-4 overflow-hidden"
              role="log"
              aria-label="Conversation"
              aria-busy={isWaitingForResponse || undefined}
            >
              {agentThread ? (
                <AgentMessageList
                  sessionId={currentSessionId}
                  messages={messages ?? []}
                  controller={agentThread}
                  busy={isWaitingForResponse || agentThread.isStreaming}
                  serverPending={isWaitingForResponse && !legacyReplyLive}
                  userAvatar={
                    <UserAvatar email={user?.email || ''} name={user?.displayName || 'User'} avatarUrl={user?.avatarUrl || ''} size={32} className="" />
                  }
                  renderLegacyMessage={renderMessage}
                  renderAttachments={(message) =>
                    message?.metadata?.unsplash?.images?.length > 0 ? <UnsplashGrid meta={message.metadata} /> : null
                  }
                  feedback={
                    showFeedbackUi
                      ? {
                          ratings: Object.fromEntries(Object.entries(replayFeedbackMap).map(([id, f]) => [id, f.rating])),
                          submit: (messageId, rating) => submitFeedbackMutation.mutate({ messageId, rating }),
                          pendingMessageId: submitFeedbackMutation.isPending ? submitFeedbackMutation.variables?.messageId : null,
                        }
                      : undefined
                  }
                  onFollowUp={(text) => onResendPrompt?.(text)}
                  streamingRef={streamingMessageRef}
                />
              ) : (
                messages?.map(renderMessage)
              )}
              
              {/* Show streaming message or loading indicator when waiting for response */}
              {/* Hide when refetch is done (persisted message is ready) */}
              {/* Wrapped with StreamingMessageWrapper to show top of response with scroll-to-bottom indicator */}
              {(!agentThread || legacyReplyLive) && isWaitingForResponse && !streamingRefetchDone && (
                <div ref={streamingMessageRef}>
                  {streamingState && streamingState.phase !== 'idle' ? (
                    <StreamingMessageWrapper isStreaming={streamingState.isStreaming}>
                      <StreamingMessage state={streamingState} />
                    </StreamingMessageWrapper>
                  ) : simulatedStreamingContent !== undefined ? (
                    // [SIMULATED_STREAMING] Progressive text display for REST-generated AI responses
                    <StreamingMessageWrapper isStreaming={true}>
                      <div className="relative mb-6 bg-white dark:bg-gray-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                            <Sparkles className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="prose dark:prose-invert max-w-none text-sm">
                              <ReactMarkdown>{simulatedStreamingContent || ''}</ReactMarkdown>
                            </div>
                            {/* Blinking cursor */}
                            <span className="inline-block w-0.5 h-4 bg-orange-500 animate-pulse ml-0.5 align-middle" />
                          </div>
                        </div>
                      </div>
                    </StreamingMessageWrapper>
                  ) : (
                    <div className="relative mb-6 bg-white dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                            <span className="text-sm text-gray-500">AIVIA is thinking...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </ScrollArea>
      
      {/* Scroll to bottom button - centered, right above the command bar */}
      {showScrollToBottom && (
        <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-6 pointer-events-none z-50">
          <button
            onClick={handleScrollToBottom}
            className="w-9 h-9 flex items-center justify-center bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full border border-gray-300 dark:border-gray-600 transition-all duration-150 pointer-events-auto"
            title="Scroll to bottom"
          >
            <ChevronDown className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      )}
      
      {/* Floating version selector removed */}
      {/* Floating selection toolbar */}
      {editSelection && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded shadow-md p-1 flex gap-1"
          style={{ top: editSelection.rect.top - 40, left: editSelection.rect.left }}
        >
          <button className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" onClick={() => handleRewrite('shorter')}>Shorter</button>
          <button className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" onClick={() => handleRewrite('formal')}>Formal</button>
          <button className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" onClick={() => handleRewrite('grammar')}>Fix grammar</button>
          <button className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" onClick={() => setShowCustomRewrite(true)}>Rewrite…</button>
          <button className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" onClick={() => { if (editSelection) undoMutation.mutate(editSelection.messageId); }}>Undo</button>
          <button className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" onClick={() => setEditSelection(null)}>Cancel</button>
        </div>
      )}
      {isSystemAdminUser && (
        <Sheet
          open={Boolean(traceInspector)}
          onOpenChange={(open) => {
            if (!open) {
              handleTraceDetailsClose();
            }
          }}
        >
          {traceInspector ? (
            <SheetContent
              side="right"
              className="w-full overflow-y-auto sm:max-w-xl"
            >
              <SheetHeader>
                <SheetTitle>Execution timeline</SheetTitle>
                <SheetDescription>
                  {traceInspector.preview}
                </SheetDescription>
              </SheetHeader>
              {traceInspector.correlationId ? (
                <div className="mt-4 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 p-3 text-xs text-muted-foreground">
                  Correlation ID:{' '}
                  <span className="font-mono text-xs">
                    {traceInspector.correlationId}
                  </span>
                </div>
              ) : null}
              <div className="mt-4 space-y-4">
                <ToolCallTimeline
                  toolCalls={traceInspector.toolCalls}
                  tokenUsage={traceInspector.tokenUsage}
                  cost={traceInspector.cost}
                  showCostSummary
                />
              </div>
            </SheetContent>
          ) : null}
        </Sheet>
      )}
      {/* Custom rewrite dialog */}
      <AlertDialog open={showCustomRewrite} onOpenChange={setShowCustomRewrite}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rewrite selection</AlertDialogTitle>
            <AlertDialogDescription>Provide a brief instruction for how to rewrite the selected text.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2">
            <Input value={customInstruction} onChange={(e) => setCustomInstruction(e.target.value)} placeholder="e.g., make it more persuasive and specific" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowCustomRewrite(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowCustomRewrite(false); handleRewrite('custom'); }}>Rewrite</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
